import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams, HttpResponse } from '@angular/common/http';
import { HttpHeaders } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, retry, map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { CitiesWithManagerCode, Countries } from '../Models/Countries';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { AgencyMasterProperties } from '../Models/AgencyMasterProperties';
export interface UmraPackageProperties {
  id: number;
  packageId: number;
  badgeText: string;
  isActive: boolean;
  availableSeats: number;
  travelDate: string;
  arrivalDate: string;
  nightsCount: number;
  airlineName: string;
  routeStart: string;
  routeEnd: string;
  departureRoute: string;
  returnRoute: string;
  outboundJourney: string;
  returnJourney: string;
  packageTitle: string;
  makkahHotel: string;
  makkahHotelAddress: string;
  makkahHotelImage: string;
  makkahHotelMapLocation: string;
  madinahHotel: string;
  madinahHotelAddress: string;
  madinahHotelImage: string;
  madinahHotelMapLocation: string;
  priceSharing: number;
  priceQuad: number;
  priceTriple: number;
  priceDouble: number;
  groupCode: string;
  packageCode: string;
  provider: string;
  creationAgencyId: string;
  reservationAgencyId: string;
  creationUserId: number;
  reservationUserId: number;
  creationDate: string;
  offerStatus: string;
  approvalUserId: string;
  departureETD: string;
  departureETA: string;
  arrivalETD: string;
  arrivalETA: string;
  departureFligntNo: string;
  arrivalFligntNo: string;
  discountPerPassenger: number;
  serviceChargesPerPassenger: number;
  providerType: string;
  supplierLetter: string;
  customPackageId: string;
  makkahZayarat: boolean;
  madinahZayarat: boolean;
  taifZayarat: boolean;
  markupOnSupplier: number;
  discountOnSupplier: number;
}

export interface UmraPackageAPIResponse {
  umraPackagesList: UmraPackageProperties[];
  success: boolean;
  message: string;
}
export interface PackageStatusUpdateDto {
  isActive?: boolean;
  offerStatus?: string;
}
export interface ShortlistRequest {
  package: ShortlistedPackage;
  customerName: string;
  email: string;
  phoneNumber: string;
  preferredRoomType: string;
  numberOfPassengers: number;
  specialRequirements: string;
  remarks: string;
  passengers: UmraPassengerInfo[];
}

export interface ShortlistResponse {
  success: boolean;
  message: string;
  shortlistId?: number;
}
export interface ShortlistedPackage {
  id: number;
  packageId: number;
  packageTitle: string;
  airlineName: string;
  routeStart: string;
  routeEnd: string;
  travelDate: string;
  arrivalDate: string;
  nightsCount: number;
  makkahHotel: string;
  madinahHotel: string;
  priceSharing: number;
  priceDouble: number;
  priceTriple: number;
  priceQuad: number;
  availableSeats: number;
  badgeText: string;
  groupCode: string;
  packageCode: string;
  provider: string;
  isActive: boolean;
  agencyId: string;
  userId: number;
  departureETD: string;
  departureETA: string;
  arrivalETD: string;
  arrivalETA: string;
  departureFligntNo: string;
  arrivalFligntNo: string;
  discountPerPassenger: number;
  serviceChargesPerPassenger: number;
  netFromCustomer: number;
  // User input fields
  customerName: string;
  email: string;
  phoneNumber: string;
  preferredRoomType: string; // Sharing, Double, Triple, Quad
  numberOfPassengers: number;
  specialRequirements: string;
  remarks: string;
  shortlistStatus: string; // Pending, Reviewed, Confirmed, Cancelled

  shortlistedDate: Date;
  createdDate: Date;

  customPackageId: string;
  providerType: string;
  supplierLetter: string;
  passengers: UmraPassengerInfo[];
}
export interface UmraPassengerInfo {
  id: number;
  umraPassengerId: string;
  passportNumber: string;
  passportIssueDate: string;  // ISO string
  passportExpiryDate: string;
  surname: string;
  givenName: string;
  dateOfBirth: string;
  childWithoutBed: boolean;
  infantWithoutBed: boolean;
  gender: string;
}
export interface ApprovalStatistics {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  todayCount: number;
  thisWeekCount: number;
  averagePrice: number;
}

export interface ApprovalResponse {
  success: boolean;
  data?: ShortlistedPackage[];
  totalCount?: number;
  pendingCount?: number;
  approvedCount?: number;
  rejectedCount?: number;
  message?: string;
  statistics?: ApprovalStatistics;
  status?: string;
}
export interface CustomPackageResponse {
  success: boolean;
  message: string;
  packageId?: number;
  count?: number;
}
export interface Hotels {
  hotelId: number;
  hotelName: string;
  hotelType: string;
  hotelCity: string;
  hotelAddress: string;
  hotelImage: string;
  hotelMapLocation: string;
}
export interface PackageUpdateResponse {
  success: boolean;
  message: string;
  packageId?: number;
}
export interface AgencyOnlineResponse {
  agency: AgencyMasterProperties;
  success: boolean;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class UmraPackagesService {
  baseUrl = environment.baseUrl;
  backSystemUrl = environment.backSystemBaseUrl;
  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  }
  constructor(private http: HttpClient, private ngZone: NgZone) { }
  // Get packages from DATABASE (main endpoint for display)
  getAllPackages(): Observable<UmraPackageAPIResponse> {
    return this.http.get<UmraPackageAPIResponse>(
      `${this.baseUrl}/UmraPackages/packages-offers`
    );
  }
  // Trigger manual sync (calls external API, saves to DB)
  syncNow(): Observable<UmraPackageAPIResponse> {
    return this.http.post<UmraPackageAPIResponse>(
      `${this.baseUrl}/UmraPackages/sync-now`,
      {}
    );
  }
  // Insert or update single package
  insertOrUpdateSinglePackage(packageOffer: UmraPackageProperties): Observable<UmraPackageAPIResponse> {
    return this.http.post<UmraPackageAPIResponse>(
      `${this.baseUrl}/UmraPackages/insert-single`,
      packageOffer
    );
  }

  // Insert or update batch of packages
  insertOrUpdatePackageBatch(multiplePackagesOffers: UmraPackageProperties[]): Observable<UmraPackageAPIResponse> {
    return this.http.post<UmraPackageAPIResponse>(
      `${this.baseUrl}/UmraPackages/insert-batch`,
      multiplePackagesOffers
    );
  }
  // Update package status
  updatePackageStatus(packageId: number, statusUpdate: PackageStatusUpdateDto): Observable<UmraPackageAPIResponse> {
    return this.http.put<UmraPackageAPIResponse>(
      `${this.baseUrl}/UmraPackages/update-package-status/${packageId}`,
      statusUpdate
    );
  }
  shortlistPackage(data: ShortlistRequest): Observable<ShortlistResponse> {
    return this.http.post<ShortlistResponse>(
      `${this.baseUrl}/UmraPackages/shortlist-package`, data
    );
  }
  getPendingApprovals(status?: string, searchTerm?: string): Observable<ApprovalResponse> {
    let params = new HttpParams();
    if (status) params = params.set('status', status);
    if (searchTerm) params = params.set('searchTerm', searchTerm);

    return this.http.get<ApprovalResponse>(`${this.baseUrl}/UmraPackages/pending-approvals`, { params });
  }

  getApprovalStatistics(): Observable<ApprovalResponse> {
    return this.http.get<ApprovalResponse>(`${this.baseUrl}/UmraPackages/statistics`);
  }

  approvePackage(id: number, request: { remarks: string }): Observable<any> {
    return this.http.put(`${this.baseUrl}/UmraPackages/approve/${id}`, request);
  }

  rejectPackage(id: number, request: { rejectionReason: string }): Observable<any> {
    return this.http.put(`${this.baseUrl}/UmraPackages/reject/${id}`, request);
  }
  // Add custom single package
  addCustomPackage(pkg: UmraPackageProperties): Observable<CustomPackageResponse> {
    return this.http.post<CustomPackageResponse>(
      `${this.baseUrl}/UmraPackages/add-single`,
      pkg
    );
  }

  // Add batch packages
  addBatchPackages(packages: UmraPackageProperties[]): Observable<CustomPackageResponse> {
    return this.http.post<CustomPackageResponse>(
      `${this.baseUrl}/UmraPackages/add-batch`,
      packages
    );
  }

  // Duplicate package
  duplicatePackage(packageId: number): Observable<CustomPackageResponse> {
    return this.http.post<CustomPackageResponse>(
      `${this.baseUrl}/UmraPackages/duplicate/${packageId}`,
      {}
    );
  }

  // Delete custom package
  //deleteCustomPackage(packageId: number): Observable<CustomPackageResponse> {
  //  return this.http.delete<CustomPackageResponse>(
  //    `${this.baseUrl}/UmraPackages/delete/${packageId}`
  //  );
  //}

  getHotels(): Observable<any> {
    return this.http.get(`${this.baseUrl}/UmraPackages/get-hotels`);
  }

  updatePackage(selectedPackage: UmraPackageProperties): Observable<PackageUpdateResponse> {
    return this.http.put<PackageUpdateResponse>(
      `${this.baseUrl}/UmraPackages/update-single`,
      selectedPackage
    );
  }

  deleteCustomPackage(packageId: number): Observable<PackageUpdateResponse> {
    return this.http.delete<PackageUpdateResponse>(
      `${this.baseUrl}/UmraPackages/delete/${packageId}`
    );
  }

  togglePackageStatus(packageId: number, isActive: boolean): Observable<PackageUpdateResponse> {
    return this.http.put<PackageUpdateResponse>(
      `${this.baseUrl}/UmraPackages/toggle-status/${packageId}`,
      { isActive: isActive }
    );
  }

  getOnlineAgency(agencyId: string): Observable<AgencyOnlineResponse> {
    return this.http.get<AgencyOnlineResponse>(
      `${this.baseUrl}/UmraPackages/agency-online/${agencyId}`
    );
  }

  async generatePdf(elementId: string, filename: string): Promise<void> {
    const element = document.getElementById(elementId);
    if (!element) {
      console.error('PDF element not found');
      return;
    }

    // Run heavy work outside Angular zone to prevent change detection blockage
    return this.ngZone.runOutsideAngular(async () => {
      try {
        // 1. Wait for Angular to render the Base64 string to the DOM
        await this.waitForImagesToLoad();

        // 2. Give the browser one animation frame to paint the pixels
        await new Promise(resolve => requestAnimationFrame(resolve));

        const canvas = await html2canvas(element, {
          scale: 1.5,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#F7F3EA',
          logging: false,
          imageTimeout: 0
        });
        
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4',
          compress: true
        });

        const pageWidth = 210;
        const pageHeight = 297;
        const margin = 5;
        const contentWidth = pageWidth - (margin * 2);
        const contentHeight = pageHeight - (margin * 2);
        const pxPerMm = canvas.width / pageWidth;
        const canvasHeightMm = canvas.height / pxPerMm;
        const totalPages = Math.ceil(canvasHeightMm / contentHeight);

        for (let page = 0; page < totalPages; page++) {
          // Yield to UI thread between pages
          if (page > 0) {
            await this.delay(0);
          }

          const sourceY = page * contentHeight * pxPerMm;
          const sliceHeightPx = Math.min(
            canvas.height - sourceY,
            contentHeight * pxPerMm
          );
          const sliceHeightMm = sliceHeightPx / pxPerMm;

          const sliceCanvas = document.createElement('canvas');
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = sliceHeightPx;

          const ctx = sliceCanvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(
              canvas,
              0, sourceY,
              canvas.width, sliceHeightPx,
              0, 0,
              canvas.width, sliceHeightPx
            );
          }

          const sliceData = sliceCanvas.toDataURL('image/jpeg', 0.85);

          if (page > 0) {
            pdf.addPage();
          }

          pdf.addImage(
            sliceData,
            'JPEG',
            margin,
            margin,
            contentWidth,
            sliceHeightMm,
            `page-${page}`,
            'FAST'
          );
        }

        pdf.save(filename);
      } catch (error) {
        console.error('PDF generation failed:', error);
        throw error;
      }
    });
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
  private waitForImagesToLoad(): Promise<void> {
    const images = Array.from(document.querySelectorAll('img'));

    const promises = images.map(img => {
      // If the image has no source, or the browser has already finished attempting to load it 
      // (whether successfully or as a broken image), resolve immediately.
      if (!img.src || img.complete) {
        return Promise.resolve();
      }

      return new Promise<void>((resolve) => {
        // Add a hard timeout to prevent the loader from spinning forever
        const timeout = setTimeout(() => {
          resolve();
        }, 3000); // Maximum 3-second wait per image

        img.addEventListener('load', () => {
          clearTimeout(timeout);
          resolve();
        }, { once: true });

        img.addEventListener('error', () => {
          clearTimeout(timeout);
          resolve();
        }, { once: true });
      });
    });

    return Promise.all(promises).then(() => { });
  }
}
